/**
 * Maps geolocation to pixel coordinates on a map
 */

import type { MapTemplateGeopoint } from "../types";
import type { LocationEstimate } from "../support/kalman";
import { derived, writable } from "svelte/store";
import { geolocation } from "../services/geolocation";
import { linear, type Predictor } from "../support/linear-regression";
import { radians, rotatePoint } from "../support/geometry";

export const avoidHazards = writable(false);

export type Prediction = {
  x: number;
  y: number;
  gravity: number;
  shape: string;
};

function predict(
  rotation: number,
  trainX: Predictor,
  trainY: Predictor,
  latitude: number,
  longitude: number
): Prediction {
  const point = rotatePoint(
    {
      x: trainX.predict(latitude)[1],
      y: trainY.predict(longitude)[1]
    },
    -rotation
  );

  return { ...point, gravity: 20, shape: "circle" };
}

type LocateOptions = {
  rotation: number;
  trainX: Predictor;
  trainY: Predictor;
};

function locate({ rotation, trainX, trainY }: LocateOptions) {
  return derived(geolocation, ($geolocation, set) => {
    if ($geolocation == null) {
      set(null);
    } else {
      const { latitude, longitude } = $geolocation as LocationEstimate;

      const guess = predict(rotation, trainX, trainY, latitude, longitude);

      set(guess);
    }
  });
}

export function trainLocator({
  angle,
  geopoints
}: {
  angle: number;
  geopoints: Array<MapTemplateGeopoint>;
}) {
  const rotation = radians(angle);

  const rotated = geopoints.map((location) => {
    return {
      position: rotatePoint(location.position, rotation),
      geopoint: location.geopoint
    };
  });

  const trainX = linear(rotated.map((i) => [i.geopoint.x, i.position.x]));
  const trainY = linear(rotated.map((i) => [i.geopoint.y, i.position.y]));

  return locate({ rotation, trainX, trainY });
}
